import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ADMIN_API_PREFIX, BASE_URL } from '@CONSTANTS/API.constant';
import userRepository from '@REPOSITORIES/local-repository/user-repository';
import { Brand, CreateBrandParams, UpdateBrandParams } from '@INTERFACES/api/brands';

// Helper function to check if params contain a File object
const hasFileUpload = (params: any): boolean => Object.values(params).some((value) => value instanceof File);

// Helper function to prepare request body
const prepareRequestBody = (params: any) => {
    if (hasFileUpload(params)) {
        const formData = new FormData();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                if (value instanceof File) {
                    formData.append(key, value);
                } else {
                    formData.append(key, String(value));
                }
            }
        });
        return formData;
    }
    return JSON.stringify(params);
};

export const brandsAdminApi = createApi({
    reducerPath: 'brands-admin-api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL + ADMIN_API_PREFIX,
        prepareHeaders: (headers) => {
            const token = userRepository.getData();

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    tagTypes: ['Brand'],
    endpoints: (builder) => ({
        getAll: builder.query<Brand[], void>({
            query: () => '/brands/',
            providesTags: ['Brand'],
        }),
        getById: builder.query<Brand, number>({
            query: (id) => `/brands/${id}/`,
            providesTags: (result, error, id) => [{ type: 'Brand', id }],
        }),
        create: builder.mutation<Brand, CreateBrandParams>({
            query: (params) => {
                const body = prepareRequestBody(params);
                const isFormData = hasFileUpload(params);
                return {
                    url: '/brands/',
                    method: 'POST',
                    body,
                    headers: isFormData
                        ? {} // Don't set Content-Type for FormData - let the browser set it with boundary
                        : { 'Content-Type': 'application/json' },
                };
            },
            invalidatesTags: ['Brand'],
        }),
        update: builder.mutation<Brand, UpdateBrandParams>({
            query: ({ id, ...params }) => {
                const body = prepareRequestBody(params);
                const isFormData = hasFileUpload(params);
                return {
                    url: `/brands/${id}/`,
                    method: 'PUT',
                    body,
                    headers: isFormData
                        ? {} // Don't set Content-Type for FormData - let the browser set it with boundary
                        : { 'Content-Type': 'application/json' },
                };
            },
            invalidatesTags: (result, error, { id }) => [{ type: 'Brand', id }],
        }),
        partialUpdate: builder.mutation<Brand, UpdateBrandParams>({
            query: ({ id, ...params }) => {
                const body = prepareRequestBody(params);
                const isFormData = hasFileUpload(params);
                return {
                    url: `/brands/${id}/`,
                    method: 'PATCH',
                    body,
                    headers: isFormData
                        ? {} // Don't set Content-Type for FormData - let the browser set it with boundary
                        : { 'Content-Type': 'application/json' },
                };
            },
            invalidatesTags: (result, error, { id }) => [{ type: 'Brand', id }],
        }),
        delete: builder.mutation<void, number>({
            query: (id) => ({
                url: `/brands/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Brand'],
        }),
    }),
});

export const {
    
    
    useCreateMutation: useCreateBrand,
    useUpdateMutation: useUpdateBrand,
    
    useDeleteMutation: useDeleteBrand,
} = brandsAdminApi;
